import React, {useEffect} from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {
  GetDatasetColumns,
  GetDatasetColumnsVariables,
} from 'data-frames/graphql/__generated/GetDatasetColumns';
import {GET_DATASET_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import StepConfigEditorPane from 'pipelines/generalized-step-config-editor/StepConfigEditorPane';
import {
  ConfigDataFramesInfos,
  getInputDataFramesFromInfos,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {InputDataFrames} from 'pipelines/StepDataUtils';
import {CenteredSpinner} from 'ui-library/Spinner';
import {JSONValue} from 'util/Types';

import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';

function isConfigComplete(
  config: Record<string, JSONValue> | null,
  inputDataFrames: InputDataFrames<PipelineStepType.APPLY_COLUMN_MAPPING, 0>
): boolean {
  if (config == null) {
    return false;
  }
  const {isComplete, errors} = validatePipelineStepConfig(
    PipelineStepType.APPLY_COLUMN_MAPPING,
    0,
    config,
    inputDataFrames
  );
  return isComplete === true && errors.length === 0;
}

/**
 * Fetches columns for the given dataset and returns a `DataFrameSpec`, or `null` if loading or inaccessible.
 */
function useDatasetColumns(datasetId: string): DataFrameSpec | null {
  const result = useBenchlingQuery<GetDatasetColumns, GetDatasetColumnsVariables>(GET_DATASET_COLUMNS_QUERY, {
    variables: {datasetId},
  });

  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const {dataset} = result.data;
  if (dataset.__typename === 'InaccessibleInfoNode') {
    throw new Error('This dataset could not be loaded.');
  }
  const {dataFrame} = dataset;
  if (dataFrame.__typename === 'InaccessibleInfoNode') {
    throw new Error('This dataset could not be loaded.');
  }
  return {
    name: dataFrame.name,
    columns: dataFrame.allColumnsJson as Array<DataFrameColumnDef>,
  };
}

interface ConfigureColumnMappingPhaseProps {
  datasetId: string;
  config: Record<string, JSONValue> | null;
  setConfig: (config: Record<string, JSONValue>) => void;
  onBack: () => void;
  onSave: () => void;
}

/**
 * The second phase of the `AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE` sidebar, in which the user
 * configures an `APPLY_COLUMN_MAPPING` step.
 *
 * An `APPLY_COLUMN_MAPPING` is added if the user creates a variable input table which does not
 * require the end user to manually map columns (`requireColumnMapping: false`), the sidebar will progress to
 * `ConfigureColumnMappingPhase`, to configure a `PipelineStepType.APPLY_COLUMN_MAPPING` step within the
 * template.
 *
 * @package
 */
export default function ConfigureColumnMappingPhase({
  datasetId,
  config,
  setConfig,
  onBack,
  onSave,
}: ConfigureColumnMappingPhaseProps): JSX.Element {
  const dataFrameSpec = useDatasetColumns(datasetId);

  useEffect(() => {
    if (dataFrameSpec != null && config == null) {
      const outputColumns: PipelineStepConfig<PipelineStepType.APPLY_COLUMN_MAPPING, 0>['outputColumns'] =
        dataFrameSpec.columns.map((column) => ({
          matchBy: 'NAME' as const,
          caseSensitive: true,
          displayName: column.displayName,
          systemName: column.systemName,
          createEmptyColumnIfNotFound: false,
        }));
      setConfig({keepUnmappedColumns: true, outputColumns});
    }
  }, [config, dataFrameSpec, setConfig]);

  const dataFramesInfos: ConfigDataFramesInfos<PipelineStepType.APPLY_COLUMN_MAPPING, 0> = {
    [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: dataFrameSpec != null ? [{dataFrameSpec}] : [],
  };
  const inputDataFrames = getInputDataFramesFromInfos(dataFramesInfos);
  const isComplete = isConfigComplete(config, inputDataFrames);

  return (
    <RightSidebarSkeleton
      title="Create table from dataset"
      confirmationButtons={[
        {
          text: 'Back',
          onClick: onBack,
          dataTestAttrsElement: 'back',
        },
        {
          text: 'Save',
          onClick: onSave,
          dataTestAttrsElement: 'save',
          disabled: !isComplete,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.WIDE}
      dataTestAttrs={{
        component: 'ConfigureColumnMappingPhase',
        element: 'root',
      }}
    >
      {dataFrameSpec == null || config == null ? (
        <CenteredSpinner />
      ) : (
        <StepConfigEditorPane
          stepType={PipelineStepType.APPLY_COLUMN_MAPPING}
          stepVersion={0}
          config={config}
          setConfig={setConfig}
          dataFramesInfos={dataFramesInfos}
          clientStateId={datasetId}
        />
      )}
    </RightSidebarSkeleton>
  );
}
